import { useEffect, useSyncExternalStore } from 'react';
import { api } from './api';

// Phone notifications (Web Push). The service worker (public/push-sw.js, loaded by the PWA's sw.js) shows them.
// Android and desktop browsers can turn alerts on from a normal tab; an iPhone only once the app is added to
// the Home Screen and opened from there (iOS 16.4 or newer).

const KEY = (import.meta.env.VITE_VAPID_PUBLIC_KEY as string | undefined)?.trim().replace(/=+$/, '') || '';
const FLAG = 'one10:push';
const SNOOZE = 'one10:alerts-snooze';

/** ok: can turn on here. ios-install: add to Home Screen first. unsupported: this browser can't. off: not set up. */
export type PushSupport = 'ok' | 'ios-install' | 'unsupported' | 'off';

export const isIOS = () =>
  /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
export const isStandalone = () =>
  window.matchMedia?.('(display-mode: standalone)').matches || (navigator as Navigator & { standalone?: boolean }).standalone === true;

export function pushSupport(): PushSupport {
  if (!KEY) return 'off';
  if (isIOS() && !isStandalone()) return 'ios-install';
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window ? 'ok' : 'unsupported';
}

const b64u = (b: Uint8Array) => btoa(String.fromCharCode(...b)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
const unb64u = (s: string) => Uint8Array.from(atob(s.replace(/-/g, '+').replace(/_/g, '/') + '=='.slice(0, (4 - (s.length % 4)) % 4)), (c) => c.charCodeAt(0));
const flag = (on?: boolean) => {
  try {
    if (on === undefined) return localStorage.getItem(FLAG) === 'on';
    if (on) localStorage.setItem(FLAG, 'on'); else localStorage.removeItem(FLAG);
  } catch { /* storage unavailable */ }
  return !!on;
};

/** The service worker, if the app has one (production builds). Gives up after a few seconds. */
async function registration(): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) return null;
  return Promise.race([navigator.serviceWorker.ready, new Promise<null>((r) => setTimeout(() => r(null), 5000))]);
}

async function currentSubscription(): Promise<PushSubscription | null> {
  const reg = await registration();
  return reg ? reg.pushManager.getSubscription() : null;
}

/** False when the subscription was made with another VAPID key (keys were changed): it can't receive ours. */
function ourKey(sub: PushSubscription): boolean {
  const k = sub.options?.applicationServerKey;
  return !k || b64u(new Uint8Array(k)) === KEY;
}

async function register(sub: PushSubscription) {
  const j = sub.toJSON();
  await api.pushSubscribe(sub.endpoint, j.keys?.p256dh ?? '', j.keys?.auth ?? '');
}

// --- State for the UI ------------------------------------------------------------------------------

export interface PushState {
  support: PushSupport;
  permission: NotificationPermission | 'n/a';
  /** This browser has a subscription (null while checking). */
  on: boolean | null;
}

let state: PushState = { support: 'off', permission: 'n/a', on: null };
const listeners = new Set<() => void>();
const emit = (next: PushState) => { state = next; listeners.forEach((l) => l()); };

async function refresh() {
  const support = pushSupport();
  const permission = 'Notification' in window ? Notification.permission : 'n/a';
  if (support !== 'ok') return emit({ support, permission, on: false });
  const sub = await currentSubscription().catch(() => null);
  emit({ support, permission, on: !!sub && permission === 'granted' && ourKey(sub) });
}

export function usePush(): PushState {
  const snap = useSyncExternalStore((l) => { listeners.add(l); return () => listeners.delete(l); }, () => state);
  useEffect(() => { void refresh(); }, []);
  return snap;
}

/** Call from a tap: asks for permission, subscribes this browser and registers it for the signed-in email. */
export async function enablePush(): Promise<'on' | 'denied' | 'dismissed' | 'unavailable'> {
  if (pushSupport() !== 'ok') return 'unavailable';
  // Ask before anything else is awaited: Safari only shows the prompt straight from the tap.
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    void refresh();
    return permission === 'denied' ? 'denied' : 'dismissed';
  }
  const reg = await registration();
  if (!reg) return 'unavailable';
  let sub = await reg.pushManager.getSubscription();
  if (sub && !ourKey(sub)) {
    await sub.unsubscribe().catch(() => {});
    sub = null;
  }
  // The browser asks its push service (Google, Apple, Mozilla) for an address; don't spin forever offline.
  sub ??= await Promise.race([
    reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: unb64u(KEY) }),
    new Promise<null>((r) => setTimeout(() => r(null), 30_000)),
  ]);
  if (!sub) return 'unavailable';
  await register(sub);
  flag(true);
  await refresh();
  return 'on';
}

export async function disablePush() {
  const sub = await currentSubscription().catch(() => null);
  if (sub) {
    await api.pushUnsubscribe(sub.endpoint).catch(() => {});
    await sub.unsubscribe().catch(() => {});
  }
  flag(false);
  await refresh();
}

export async function testPush(): Promise<number> {
  const sub = await currentSubscription();
  if (!sub) throw new Error('push_not_subscribed');
  return api.pushTest(sub.endpoint);
}

/**
 * When the app opens signed in: keep this phone registered (keys can change, and the email may be new on a
 * shared phone). Re-subscribes quietly only where alerts were turned on before and permission still stands.
 */
export async function syncPush() {
  if (pushSupport() !== 'ok' || Notification.permission !== 'granted') return;
  try {
    let sub = await currentSubscription();
    if (sub && !ourKey(sub)) {
      await sub.unsubscribe().catch(() => {});
      sub = null;
    }
    if (!sub && flag()) {
      const reg = await registration();
      sub = reg ? await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: unb64u(KEY) }) : null;
    }
    if (sub) await register(sub);
  } catch { /* try again next time */ }
  void refresh();
}

/** On sign-out: this phone stops getting the flat's alerts (the browser keeps its permission). */
export async function forgetPushDevice() {
  const sub = await Promise.race([currentSubscription(), new Promise<null>((r) => setTimeout(() => r(null), 1500))]).catch(() => null);
  if (sub) await api.pushUnsubscribe(sub.endpoint).catch(() => {});
}

// --- "Not now" on the alerts card --------------------------------------------------------------------

export function alertsSnoozed(): boolean {
  try { return Number(localStorage.getItem(SNOOZE) || 0) > Date.now(); } catch { return false; }
}
export function snoozeAlerts(days = 2) {
  try { localStorage.setItem(SNOOZE, String(Date.now() + days * 86400_000)); } catch { /* storage unavailable */ }
  emit({ ...state });
}

// --- "Install app" (Android and desktop Chrome) ------------------------------------------------------

interface InstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}
let deferred: InstallPromptEvent | null = null;
const installListeners = new Set<() => void>();

/** Call once at startup: the browser offers the install prompt early, before any page has mounted. */
export function captureInstallPrompt() {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    deferred = e as InstallPromptEvent;
    installListeners.forEach((l) => l());
  });
  window.addEventListener('appinstalled', () => {
    deferred = null;
    installListeners.forEach((l) => l());
  });
}

export function useInstallPrompt() {
  const can = useSyncExternalStore((l) => { installListeners.add(l); return () => installListeners.delete(l); }, () => !!deferred);
  return {
    canInstall: can && !isStandalone(),
    install: async () => {
      if (!deferred) return false;
      await deferred.prompt();
      const { outcome } = await deferred.userChoice;
      deferred = null;
      installListeners.forEach((l) => l());
      return outcome === 'accepted';
    },
  };
}
